
import { Modal } from 'bootstrap';
import { defineComponent, ref, inject, computed, onUnmounted } from 'vue';
import { Apollo, Notify } from '@/core/services';
import { UPDATE_PRODUCT_PRODUCT_GROUP } from '@/modules/catalog/products/graphql/Mutations';
import { GET_PRODUCT_PRODUCT_GROUPS } from '@/modules/catalog/products/graphql/Queries';
import { useI18n } from 'vue-i18n';
import InnerLoader from '@/components/InnerLoader.vue';

interface ProductRow {
    product_id?: number;
    id?: number;
    product_group_id?: number;
    sale_price?: number | string | null;
    discount_type?: string | null;
    discount_value?: number | string | null;
    assignment_id?: number | null;
}

export default defineComponent({
    name: 'product-group-discount-modal',
    components: {
        InnerLoader
    },
    setup() {
        const modal: any = ref();
        const loading = ref(false);
        const emitter: any = inject('emitter');
        const i18n = useI18n();
        const discountForm = ref<null | HTMLFormElement>(null);

        const formData = ref({
            product_id: null as number | null,
            product_group_id: null as number | null,
            assignment_id: null as number | null,
            sale_price: '' as string | number,
            discount_type: '' as string,
            discount_value: '' as string
        });

        const resolveProductId = (product: ProductRow): number => {
            return Number(product.product_id ?? product.id ?? 0);
        };

        const resolveProductGroupId = (product: ProductRow): number => {
            return Number(product.product_group_id ?? localStorage.getItem('prod_grp_id') ?? 0);
        };

        const normalizeDiscountType = (type: string | null | undefined): string => {
            if (!type) return '';
            if (type === 'value') return 'amount';
            return type;
        };

        const parseDiscountValue = (value: unknown): string => {
            if (value === null || value === undefined || value === '') return '';

            const raw = String(value).trim();
            const normalized = raw.replace(/[^\d.,-]/g, '').replace(',', '.');
            const parsed = parseFloat(normalized);

            return Number.isNaN(parsed) ? '' : String(parsed);
        };

        const fetchAssignment = async (productId: number, productGroupId: number) => {
            const result = await Apollo.query({
                query: GET_PRODUCT_PRODUCT_GROUPS,
                variables: {
                    product_id: productId,
                    page: 0,
                    limit: 100
                },
                fetchPolicy: 'network-only',
                errorPolicy: 'all'
            });

            const rows = (result.data?.product_product_groups?.data ?? []) as Record<string, unknown>[];
            const matched = rows.find(row => Number(row.product_group_id) === Number(productGroupId));
            if (matched) return matched;
            return rows.length === 1 ? rows[0] : null;
        };

        const canSubmit = computed(() => {
            return (
                formData.value.discount_type &&
                formData.value.discount_value !== '' &&
                parseFloat(formData.value.discount_value) > 0
            );
        });

        const resetForm = () => {
            formData.value = {
                product_id: null,
                product_group_id: null,
                assignment_id: null,
                sale_price: '',
                discount_type: '',
                discount_value: ''
            };
        };

        const submitHandler = async () => {
            if (!canSubmit.value || !formData.value.product_id || !formData.value.product_group_id) {
                Notify.error(i18n.t('message.FILL_ALL_MISSING_FIELDS'));
                return;
            }

            loading.value = true;

            await Apollo.mutate({
                mutation: UPDATE_PRODUCT_PRODUCT_GROUP,
                variables: {
                    input: {
                        id: formData.value.assignment_id,
                        product_id: formData.value.product_id,
                        product_group_id: formData.value.product_group_id,
                        discount_type: formData.value.discount_type,
                        discount_value: parseFloat(formData.value.discount_value)
                    }
                },
                update: () => {
                    loading.value = false;
                    modal.value?.hide();
                    emitter.emit('productGroupDiscountSaved');
                    Notify.success(i18n.t('message.RECORD_UPDATED_SUCCESSFULLY'));
                }
            }).catch(() => {
                loading.value = false;
            });
        };

        const populateForm = (
            product: ProductRow,
            assignment: Record<string, unknown> | null,
            productId: number,
            productGroupId: number
        ) => {
            const assignmentId = assignment ? Number(assignment.id) || null : product.assignment_id ?? null;
            const discountType = assignment
                ? ((assignment.discount_type as string) ?? null)
                : product.discount_type ?? null;
            const discountValue = assignment
                ? parseDiscountValue(assignment.discount_value_original ?? assignment.discount_value)
                : parseDiscountValue(product.discount_value);

            formData.value = {
                product_id: productId,
                product_group_id: productGroupId,
                assignment_id: assignmentId,
                sale_price: product.sale_price ?? '',
                discount_type: normalizeDiscountType(discountType),
                discount_value: discountValue
            };
        };

        const openModal = async (product: ProductRow) => {
            loading.value = true;

            const productId = resolveProductId(product);
            const productGroupId = resolveProductGroupId(product);

            if (!productId || !productGroupId) {
                loading.value = false;
                Notify.error(i18n.t('message.FILL_ALL_MISSING_FIELDS'));
                return;
            }

            const assignment = await fetchAssignment(productId, productGroupId);
            populateForm(product, assignment, productId, productGroupId);
            loading.value = false;

            if (!modal.value) {
                modal.value = new Modal(document.getElementById('modal_product_group_discount'));
            }
            modal.value.show();
        };

        emitter.on('editProductGroupDiscountAction', openModal);

        onUnmounted(() => {
            emitter.off('editProductGroupDiscountAction', openModal);
        });

        return {
            loading,
            formData,
            discountForm,
            canSubmit,
            submitHandler
        };
    }
});
